import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ImagePlus, Sun, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { LookRef } from '../studio-contract';

/**
 * A picture whose light Studio copies — a film still, a photo from a shoot the
 * vendor likes. Only its light, grade and atmosphere are taken; the camera
 * comes from the shot (or the phone take), the product from the catalogue.
 */
const Picker: React.FC<{
    value?: LookRef;
    onChange: (v: LookRef | undefined) => void;
    /** Shown, dimmed, when this one is empty: what applies instead (the film's). */
    inherited?: LookRef;
    inheritedLabel?: string;
}> = ({ value, onChange, inherited, inheritedLabel }) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const input = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const shown = value || inherited;

    const upload = async (file?: File) => {
        if (!file) return;
        setBusy(true);
        setError('');
        try {
            const { url } = await api.uploadStyleRef(file);
            if (!url) throw new Error('no url');
            onChange({ url });
        } catch {
            setError(t('STUDIO.LIGHT.UPLOAD_FAILED', 'The picture could not be uploaded. Try another one.'));
        } finally {
            setBusy(false);
            if (input.current) input.current.value = '';
        }
    };

    return (
        <div className="space-y-2">
            {shown ? (
                <div className="relative overflow-hidden rounded-lg border border-input">
                    <img src={shown.url} alt="" className={cn('aspect-video w-full object-cover', !value && 'opacity-60')} />
                    {!value && inheritedLabel && (
                        <span className="absolute bottom-1.5 start-1.5 rounded-md bg-background/90 px-1.5 py-0.5 text-[11px] text-muted-foreground">{inheritedLabel}</span>
                    )}
                    {value && (
                        <Button type="button" variant="outline" size="icon" className="absolute end-1.5 top-1.5 size-7 bg-background/90"
                            onClick={() => onChange(undefined)} aria-label={t('STUDIO.LIGHT.REMOVE', 'Remove')}>
                            <X className="size-3.5" />
                        </Button>
                    )}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">
                    {t('STUDIO.LIGHT.EMPTY', 'None yet: each shot is lit by its look and its photo.')}
                </p>
            )}
            <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" disabled={busy} onClick={() => input.current?.click()}>
                {busy ? <Spinner className="size-3.5" /> : <ImagePlus className="size-3.5" />}
                {value ? t('STUDIO.LIGHT.REPLACE', 'Use another picture') : t('STUDIO.LIGHT.UPLOAD', 'Upload a picture')}
            </Button>
            {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
    );
};

/** The film's lighting reference: a button beside the look, opening the picker. */
export const FilmLightingButton: React.FC<{ value?: LookRef; onChange: (v: LookRef | undefined) => void }> = ({ value, onChange }) => {
    const { t } = useTranslation();
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 font-normal" aria-label={t('STUDIO.LIGHT.LABEL', 'Lighting')}>
                    {value
                        ? <img src={value.url} alt="" className="size-4 rounded-[3px] object-cover" />
                        : <Sun className="size-3.5 text-muted-foreground" />}
                    <span className="font-medium">{t('STUDIO.LIGHT.LABEL', 'Lighting')}</span>
                    <ChevronDown className="size-3 text-muted-foreground" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-2">
                <div>
                    <p className="text-sm font-medium text-foreground">{t('STUDIO.LIGHT.TITLE', 'Lighting reference')}</p>
                    <p className="text-xs text-muted-foreground">
                        {t('STUDIO.LIGHT.HINT', 'A picture whose light you like — a film still, a photo from a shoot. Every shot takes its light, colour grade and atmosphere; never its framing or furniture.')}
                    </p>
                </div>
                <Picker value={value} onChange={onChange} />
            </PopoverContent>
        </Popover>
    );
};

/** A shot's own lighting reference, over the film's. */
export const ShotLighting: React.FC<{ value?: LookRef; film?: LookRef; onChange: (v: LookRef | undefined) => void }> = ({ value, film, onChange }) => {
    const { t } = useTranslation();
    return <Picker value={value} onChange={onChange} inherited={film} inheritedLabel={t('STUDIO.LIGHT.FROM_FILM', "The film's")} />;
};
